package com.sddamico.swigmore.util

import com.sddamico.swigmore.models.GameOutcome

/** 1 → "1st", 12 → "12th", 22 → "22nd". */
fun ordinal(n: Int): String = n.toString() + when {
  n % 100 in 11..13 -> "th"
  n % 10 == 1 -> "st"
  n % 10 == 2 -> "nd"
  n % 10 == 3 -> "rd"
  else -> "th"
}

fun firstPlaceVotes(n: Int) = if (n == 1) "1 first-place vote" else "$n first-place votes"

/** [value] with exactly [digits] decimals. */
fun fixed(value: Double, digits: Int): String = value.asDynamic().toFixed(digits) as String

/** A rating between 0 and 1 without the leading zero, as box scores print them: 0.8812 → ".8812". */
fun share(value: Double, digits: Int): String = fixed(value, digits).removePrefix("0")

/** A margin around zero with its sign: 3.25 → "+3.3", -1.0 → "-1.0". */
fun signedFixed(value: Double, digits: Int): String = (if (value > 0) "+" else "") + fixed(value, digits)

private fun minutesSince(at: Long, now: Long) = ((now - at) / 60_000).coerceAtLeast(0)

/** "just now", "12m ago", "3h ago", "2d ago", for the sync chip. */
fun timeAgo(at: Long, now: Long): String {
  val minutes = minutesSince(at, now)
  return when {
    minutes < 1 -> "just now"
    minutes < 60 -> "${minutes}m ago"
    minutes < 48 * 60 -> "${minutes / 60}h ago"
    else -> "${minutes / (24 * 60)}d ago"
  }
}

/** "2M", "5H", "3D", as on the design's submission wire. */
fun wireAge(at: Long, now: Long): String {
  val minutes = minutesSince(at, now)
  return when {
    minutes < 60 -> "${minutes}M"
    minutes < 24 * 60 -> "${minutes / 60}H"
    else -> "${minutes / (24 * 60)}D"
  }
}

/** "2d 4h" until [deadline], or "3h 12m" on the last day. */
fun untilCutoff(deadline: Long, now: Long): String {
  val minutes = ((deadline - now) / 60_000).coerceAtLeast(0)
  val days = minutes / (24 * 60)
  val hours = minutes / 60 % 24
  return if (days > 0) "${days}d ${hours}h" else "${hours}h ${minutes % 60}m"
}

/** "10:30 PM" for 1350, minutes after midnight. */
fun minuteClock(minuteOfDay: Int): String {
  val hour = minuteOfDay / 60
  return "${(hour + 11) % 12 + 1}:${(minuteOfDay % 60).toString().padStart(2, '0')} ${if (hour < 12) "AM" else "PM"}"
}

/** "A, B and C" */
fun andList(items: List<String>): String =
  if (items.size < 2) items.joinToString() else "${items.dropLast(1).joinToString(", ")} and ${items.last()}"

/** The label on a game, or a table, whose scores Fantrax can still correct. */
const val NOT_FINAL = "Not final"

/** "Win", "Loss", "Tie" */
val GameOutcome.word get() = when (this) {
  GameOutcome.WIN -> "Win"
  GameOutcome.LOSS -> "Loss"
  GameOutcome.TIE -> "Tie"
}

val GameOutcome.letter get() = word.take(1)

/** "Beat Opponent", "Lost to Opponent", "Tied Opponent" */
fun GameOutcome.against(opponent: String) = when (this) {
  GameOutcome.WIN -> "Beat $opponent"
  GameOutcome.LOSS -> "Lost to $opponent"
  GameOutcome.TIE -> "Tied $opponent"
}
